
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { CITIES } from '../../config/cities';
import { trackEvent } from '../../utils/tracking';
import { generateCitiesPageSchema } from '../../utils/schema';

export default function CitiesPage() {
  useEffect(() => {
    // Set document metadata
    document.title = 'Business Funding by City | Find Local Business Capital Solutions';
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Explore business funding options in your city. Pre-qualification available for businesses in New York, Miami, Dallas, Houston, Los Angeles, Chicago, Atlanta, Phoenix, Las Vegas, and Tampa.');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${window.location.origin}/cities`);

    // Add page schema with breadcrumbs
    const existingSchema = document.querySelector('script[data-schema="cities-page"]');
    if (existingSchema) {
      existingSchema.remove();
    }
    const schemaScript = document.createElement('script');
    schemaScript.setAttribute('type', 'application/ld+json');
    schemaScript.setAttribute('data-schema', 'cities-page');
    schemaScript.textContent = JSON.stringify(generateCitiesPageSchema());
    document.head.appendChild(schemaScript);

    trackEvent('page_view', {
      page_title: 'Cities Directory',
      page_location: window.location.href,
      page_path: '/cities'
    });

    return () => {
      const schema = document.querySelector('script[data-schema="cities-page"]');
      if (schema) schema.remove();
    };
  }, []);

  const handleCityClick = (citySlug: string, cityName: string, linkType: 'hub' | 'prequalification') => {
    trackEvent('city_link_click', {
      city: citySlug,
      city_name: cityName,
      link_type: linkType,
      source_page: 'cities_directory'
    });
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white">
        {/* Breadcrumb Navigation */}
        <nav className="bg-gray-50 border-b border-gray-200" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <Link 
                  to="/" 
                  className="text-gray-600 hover:text-amber-600 transition-colors"
                >
                  Home
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li>
                <span className="text-gray-900 font-medium">Business Funding by City</span>
              </li>
            </ol>
          </div>
        </nav>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              <i className="ri-map-pin-line mr-2"></i>
              Serving Businesses Nationwide
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Business Funding by City
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Explore funding options tailored to your local market. Select your city to access city-specific resources and start your pre-qualification.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-center text-sm text-gray-300">
                <i className="ri-shield-check-line text-teal-400 mr-2 text-lg"></i>
                Educational Platform
              </div>
              <div className="hidden sm:block w-1 h-1 bg-gray-600 rounded-full"></div>
              <div className="flex items-center text-sm text-gray-300">
                <i className="ri-time-line text-teal-400 mr-2 text-lg"></i>
                Fast Pre-Qualification
              </div>
              <div className="hidden sm:block w-1 h-1 bg-gray-600 rounded-full"></div>
              <div className="flex items-center text-sm text-gray-300">
                <i className="ri-building-line text-teal-400 mr-2 text-lg"></i>
                {CITIES.length} Cities Available
              </div>
            </div>
          </div>
        </header>

        {/* Cities Grid */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Select Your City for Local Funding Information
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose your city to explore local funding options and access your personalized pre-qualification form.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {CITIES.map((city) => (
              <article
                key={city.slug}
                className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1 group-hover:text-teal-600 transition-colors">
                      {city.name}
                    </h3>
                    <p className="text-sm text-gray-500">{city.state}</p>
                  </div>
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-lg group-hover:bg-teal-100 transition-colors">
                    <i className="ri-building-2-line text-2xl text-teal-600"></i>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-6 line-clamp-2">
                  Explore funding solutions for {city.name} businesses. Learn about local market opportunities and qualification requirements.
                </p>

                <div className="space-y-3">
                  <Link
                    to={`/cities/${city.slug}/prequalification`}
                    onClick={() => handleCityClick(city.slug, city.name, 'prequalification')}
                    className="inline-flex items-center justify-center w-full px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
                  >
                    Start {city.name} Pre-Qualification
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>

                  <Link
                    to={`/cities/${city.slug}`}
                    onClick={() => handleCityClick(city.slug, city.name, 'hub')}
                    className="flex items-center justify-center w-full px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-teal-500 hover:text-teal-600 transition-all duration-300 whitespace-nowrap"
                  >
                    Learn About {city.name} Business Funding
                    <i className="ri-information-line ml-2"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* Why Choose by City Section */}
          <section className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 sm:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose City-Specific Funding Resources?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Understanding your local market helps you find the most relevant funding options for your business.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <i className="ri-map-pin-user-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Local Market Insights
                </h3>
                <p className="text-gray-600">
                  Get information tailored to your city's business environment, industries, and economic conditions.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <i className="ri-funds-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Relevant Funding Options
                </h3>
                <p className="text-gray-600">
                  Discover funding solutions that align with your local market needs and business goals.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                  <i className="ri-speed-up-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Faster Pre-Qualification
                </h3>
                <p className="text-gray-600">
                  City-specific context helps streamline your pre-qualification and application process.
                </p>
              </div>
            </div>
          </section>
        </main>

        {/* Bottom CTA */}
        <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Explore Your Funding Options?
            </h2>
            <p className="text-xl text-red-100 mb-8">
              Select your city above to start your pre-qualification or learn more about business funding.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('.grid')?.scrollIntoView({ behavior: 'smooth' });
                  trackEvent('cta_click', {
                    cta_text: 'Choose Your City',
                    cta_location: 'bottom_section',
                    page: 'cities_directory'
                  });
                }}
                className="px-8 py-4 bg-white text-red-600 rounded-lg font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Choose Your City
              </a>
              <Link
                to="/business-funding"
                onClick={() => {
                  trackEvent('cta_click', {
                    cta_text: 'Read Complete Funding Guide',
                    cta_location: 'bottom_section',
                    page: 'cities_directory'
                  });
                }}
                className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold hover:bg-white hover:text-red-600 transition-all duration-300 whitespace-nowrap"
              >
                Read Complete Funding Guide
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}
